/**
 * ConnectionForm: the device's connection (its link params) on the Protocol
 * step, rendered from the chosen driver's link_schema (web brief U4) and
 * prefilled from the discover hit: host and port, the OPC UA endpoint, the S7
 * rack / slot, the Modbus unit. Modbus RTU's serial port is picked from the
 * ports the gateway reported in its birth.
 *
 * Required fields come first; the ones almost every device leaves at the
 * default (S7 connection type, MELSEC network / PC / I/O / station, OPC UA
 * security; see linkAdvancedFields) fold under "Advanced settings", which
 * opens by itself when one of them is invalid. Each field's schema
 * description is its inline help.
 */

import { Cable } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/ui/card";
import type { JsonSchema } from "@/lib/api";
import type { FormValues } from "@/lib/iot/schema-form";
import { SchemaForm } from "@/components/shared/SchemaForm";

export interface ConnectionFormProps {
  driverLabel: string;
  schema: JsonSchema;
  values: FormValues;
  onChange: (next: FormValues) => void;
  errors: Record<string, string>;
  /** Serial ports from the gateway's birth ([] = it reported none; undefined = no birth). */
  serialPorts?: string[];
  prefilled: boolean;
  /** Optional fields to fold under "Advanced settings". */
  advanced?: readonly string[];
}

export function ConnectionForm({
  driverLabel,
  schema,
  values,
  onChange,
  errors,
  serialPorts,
  prefilled,
  advanced,
}: ConnectionFormProps) {
  const hasSerial = !!(schema as { properties?: Record<string, unknown> }).properties?.serial_port;
  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <Cable className="size-5 text-brand" />
          Connection settings
        </CardTitle>
        <CardDescription>
          {prefilled
            ? "Filled in from the scan; check it and go on."
            : `How the gateway reaches the device over ${driverLabel}.`}{" "}
          Fields marked * are required.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <SchemaForm
          schema={schema}
          values={values}
          onChange={onChange}
          errors={errors}
          choices={hasSerial && serialPorts ? { serial_port: serialPorts } : undefined}
          advanced={advanced}
          requiredFirst
        />
      </CardContent>
    </Card>
  );
}
